import { useState } from 'react';
import {
  definirModoDeIdentificacao,
  obterIdentificacao,
} from '../../api/configuracao-identificacao.js';
import type {
  ConfiguracaoDeIdentificacao,
  ModoDeIdentificacao,
} from '../../api/configuracao-identificacao.js';
import { mensagemDoErro } from '../../api/erros.js';
import { useConsulta } from '../../api/useConsulta.js';
import { usePode } from '../../autorizacao/usePode.js';
import { AvisoDaTela } from '../../componentes/AvisoDaTela.js';
import { Botao } from '../../componentes/Botao.js';
import { CampoSelecao } from '../../componentes/Campo.js';
import type { OpcaoDeCampo } from '../../componentes/Campo.js';
import { Cartao } from '../../componentes/Cartao.js';
import { DialogoConfirmacao } from '../../componentes/DialogoConfirmacao.js';
import { FalhaDeCarregamento } from '../../componentes/FalhaDeCarregamento.js';
import { formatarDataHora } from '../../componentes/datas.js';
import { CargaDeLotes } from './CargaDeLotes.js';

const ROTULO_DO_MODO: Readonly<Record<ModoDeIdentificacao, string>> = {
  PRODUTO: 'Código do produto',
  LOTE: 'Código do lote',
};

function opcoes(configuracao: ConfiguracaoDeIdentificacao): readonly OpcaoDeCampo[] {
  // Lote indisponível só quando ainda NÃO é o modo vigente: o modo em uso
  // nunca some do campo, nem que os lotes tenham deixado de existir.
  const loteIndisponivel =
    !configuracao.lotesSincronizados && configuracao.modoIdentificacaoQr !== 'LOTE';

  return [
    { valor: 'PRODUTO', rotulo: ROTULO_DO_MODO.PRODUTO },
    {
      valor: 'LOTE',
      rotulo: loteIndisponivel
        ? `${ROTULO_DO_MODO.LOTE} — sem lotes sincronizados`
        : ROTULO_DO_MODO.LOTE,
      desabilitada: loteIndisponivel,
    },
  ];
}

function EfeitoDaTroca({ modo }: { readonly modo: ModoDeIdentificacao }) {
  return modo === 'LOTE' ? (
    <>
      As etiquetas novas passam a carregar o código do lote no QR, e os novos ciclos de inventário
      contam por lote. Os ciclos de inventário abertos não mudam: continuam contando por produto até
      fecharem.
    </>
  ) : (
    <>
      As etiquetas novas voltam a carregar o código do produto no QR, e os novos ciclos de
      inventário contam por produto. Os ciclos de inventário abertos não mudam: continuam contando
      por lote até fecharem.
    </>
  );
}

/**
 * Identificação no armazém: o QR da etiqueta carrega o código do produto ou o
 * do lote, e o inventário conta do mesmo jeito.
 *
 * Supervisor lê; só quem gerencia a integração muda (a mesma regra da
 * conexão com o ERP). Perfil Consulta não vê o cartão — a API recusaria a
 * leitura.
 */
export function IdentificacaoNoArmazem() {
  const pode = usePode();
  const podeVer = pode('identificacao.ver');
  const podeGerenciar = pode('integracao.gerenciar');

  const consulta = useConsulta(podeVer ? obterIdentificacao : null);
  const [escolhido, definirEscolhido] = useState<ModoDeIdentificacao | null>(null);
  const [confirmando, definirConfirmando] = useState(false);
  const [agindo, definirAgindo] = useState(false);
  const [erroDoDialogo, definirErroDoDialogo] = useState<string | null>(null);
  const [aviso, definirAviso] = useState<string | null>(null);

  if (!podeVer) {
    return null;
  }

  const { dados, recarregar } = consulta;
  const vigente = dados?.modoIdentificacaoQr ?? null;
  const modo = escolhido ?? vigente;
  const mudou = escolhido !== null && escolhido !== vigente;

  function fecharDialogo() {
    definirConfirmando(false);
    definirErroDoDialogo(null);
  }

  function aplicar(novo: ModoDeIdentificacao) {
    definirAgindo(true);
    definirErroDoDialogo(null);

    definirModoDeIdentificacao(novo)
      .then(() => {
        definirAgindo(false);
        definirConfirmando(false);
        definirEscolhido(null);
        definirAviso(
          novo === 'LOTE'
            ? 'O armazém passa a identificar pelo código do lote.'
            : 'O armazém passa a identificar pelo código do produto.',
        );
        recarregar();
      })
      .catch((erro: unknown) => {
        definirAgindo(false);
        definirErroDoDialogo(mensagemDoErro(erro));
      });
  }

  return (
    <Cartao titulo="Identificação no armazém">
      <p className="configuracoes__nota">
        O que o QR das etiquetas carrega e como o coletor conta no inventário: pelo código do
        produto, ou pelo código do lote para quem controla lote no ERP.
      </p>

      {aviso !== null && <AvisoDaTela tom="ok">{aviso}</AvisoDaTela>}

      {consulta.erro !== null && (
        <FalhaDeCarregamento mensagem={consulta.erro} aoTentarDeNovo={recarregar} />
      )}

      {consulta.erro === null && dados === null && (
        <p className="configuracoes__detalhe" role="status">
          Carregando a identificação…
        </p>
      )}

      {dados !== null && modo !== null && (
        <>
          <div className="configuracoes__grade">
            <CampoSelecao
              rotulo="Identificar por"
              valor={modo}
              opcoes={opcoes(dados)}
              aoMudar={(valor) => {
                definirAviso(null);
                definirEscolhido(valor === 'LOTE' ? 'LOTE' : 'PRODUTO');
              }}
              desabilitado={!podeGerenciar}
              ajuda={
                dados.alteradoEm === null
                  ? 'Nunca alterado — vale o padrão, código do produto.'
                  : `Alterado em ${formatarDataHora(dados.alteradoEm)}.`
              }
            />
          </div>

          {!dados.lotesSincronizados && (
            <p className="configuracoes__nota configuracoes__nota--depois">
              Nenhum lote foi sincronizado do ERP ainda, e sem eles o coletor não teria como
              reconhecer a etiqueta de lote. A identificação por lote fica disponível depois da
              carga de lotes.
            </p>
          )}

          {podeGerenciar && !dados.lotesSincronizados && <CargaDeLotes aoConcluir={recarregar} />}

          {podeGerenciar && mudou && (
            <div className="configuracoes__acoes">
              <Botao
                variante="primario"
                onClick={() => {
                  definirConfirmando(true);
                }}
              >
                Aplicar
              </Botao>
              <Botao
                variante="discreto"
                onClick={() => {
                  definirEscolhido(null);
                }}
              >
                Desfazer
              </Botao>
            </div>
          )}
        </>
      )}

      {confirmando && escolhido !== null && (
        <DialogoConfirmacao
          titulo={
            escolhido === 'LOTE'
              ? 'Passar a identificar por lote?'
              : 'Voltar a identificar por produto?'
          }
          rotuloConfirmar={
            escolhido === 'LOTE' ? 'Identificar por lote' : 'Identificar por produto'
          }
          variante="primario"
          agindo={agindo}
          aoConfirmar={() => {
            aplicar(escolhido);
          }}
          aoCancelar={fecharDialogo}
        >
          <EfeitoDaTroca modo={escolhido} />
          {erroDoDialogo !== null && (
            <span className="configuracoes__erro-dialogo">{erroDoDialogo}</span>
          )}
        </DialogoConfirmacao>
      )}
    </Cartao>
  );
}
